/**
 * The chip editor's DOM ↔ token text mapping. The editor is a
 * `contenteditable` element holding text nodes and atomic chips
 * (`<span contenteditable="false" data-token-key="…">`); its value is the
 * framework's token text (`{{key}}` for a reference, `\n` for a line break).
 * The DOM is owned here, not by React.
 */

import type { TokenSegment } from './adapt.js';

export const CHIP_CLASS = 'ft-chip';
/** Keeps a trailing line break visible in a pre-wrap editor; never part of the value. */
const FILLER = '​';

export type ChipInfo = { label: string; title?: string };

export function createChip(doc: Document, key: string, info: ChipInfo): HTMLSpanElement {
  const span = doc.createElement('span');
  span.className = CHIP_CLASS;
  span.contentEditable = 'false';
  span.dataset.tokenKey = key;
  span.textContent = info.label;
  if (info.title) span.title = info.title;
  return span;
}

/** Replaces the editor's content with the segments. */
export function fillTokenDom(root: HTMLElement, segments: readonly TokenSegment[], chip: (key: string) => ChipInfo): void {
  const doc = root.ownerDocument;
  root.replaceChildren(
    ...segments.map((s) => (s.kind === 'text' ? doc.createTextNode(s.text) : createChip(doc, s.key, chip(s.key)))),
  );
  if (endsWithBreak(root)) root.append(doc.createTextNode(FILLER));
}

const endsWithBreak = (root: HTMLElement): boolean => {
  const last = root.lastChild;
  return last?.nodeType === 3 && (last as Text).data.endsWith('\n');
};

const BLOCK_TAGS = new Set(['DIV', 'P', 'LI']);

/**
 * The editor's value as token text. Chips → `{{key}}`; `<br>` and block
 * wrappers a browser may insert → `\n`; anything else (pasted or
 * shortcut formatting) → its text. A browser's placeholder `<br>` at the
 * very end is not a line break.
 */
export function serializeTokenDom(root: HTMLElement): string {
  let out = '';
  const walk = (node: Node, isRoot: boolean): void => {
    const children = Array.from(node.childNodes);
    children.forEach((child, i) => {
      if (child.nodeType === 3) {
        out += (child as Text).data;
        return;
      }
      if (child.nodeType !== 1) return;
      const el = child as HTMLElement;
      const key = el.dataset?.tokenKey;
      if (key) {
        out += `{{${key}}}`;
        return;
      }
      if (el.tagName === 'BR') {
        if (!(isRoot && i === children.length - 1)) out += '\n';
        return;
      }
      if (BLOCK_TAGS.has(el.tagName) && out !== '' && !out.endsWith('\n')) out += '\n';
      walk(el, false);
    });
  };
  walk(root, true);
  return out.split(FILLER).join('');
}

/**
 * The selection the editor lives in: its shadow root's where the browser
 * offers one (Chromium), else the document's (Firefox reports shadow
 * content there; elsewhere the caret is unknown and inserts go last).
 */
function selectionOf(root: HTMLElement): Selection | null {
  const host = root.getRootNode() as Document | (ShadowRoot & { getSelection?: () => Selection | null });
  const own = 'getSelection' in host && typeof host.getSelection === 'function' ? host.getSelection() : null;
  return own ?? root.ownerDocument.getSelection();
}

/** The editor's selection range, when the caret is inside it. */
export function rangeInside(root: HTMLElement): Range | null {
  const sel = selectionOf(root);
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  return root.contains(range.startContainer) && root.contains(range.endContainer) ? range.cloneRange() : null;
}

/**
 * Inserts a node at `range` (replacing the selected content) or at the end,
 * and returns the collapsed range just after it. A line break inserted last
 * gets a filler so the new line shows.
 */
export function insertAt(root: HTMLElement, range: Range | null, node: Node): Range {
  const doc = root.ownerDocument;
  const usable = range && root.contains(range.startContainer) && root.contains(range.endContainer) ? range : null;
  if (usable) {
    usable.deleteContents();
    usable.insertNode(node);
  } else {
    if (root.lastChild?.nodeType === 3 && (root.lastChild as Text).data === FILLER) root.lastChild.remove();
    root.append(node);
  }
  if (endsWithBreak(root)) root.append(doc.createTextNode(FILLER));
  const after = doc.createRange();
  after.setStartAfter(node);
  after.collapse(true);
  return after;
}

/** Puts the caret at `range`. */
export function placeCaret(root: HTMLElement, range: Range): void {
  const sel = selectionOf(root);
  if (!sel) return;
  sel.removeAllRanges();
  sel.addRange(range);
}
